import { deferShellRender } from '../ui/shell_input_guard.mjs';
import { renderActivePlanSurface } from '../render/plan_surface_runtime.mjs';
import * as RenderScheduler from '../render/render_scheduler.js?v=20260706e';
import { isPreparingProjectMutation } from '../project/project_mutation_scope.mjs';
import { AppRuntime } from './app_runtime.js?v=20260706d';
import { render2D } from '../render/plan_renderer.js?v=20260904f';
import { createPlanRenderRequest, resolvePlanRenderRequest } from '../render/plan_render_request.js';
import { isBomPlanCaptureInProgress } from '../bom/bom_plan_context.js?v=20260904c';
import { isPrintPlanCaptureInProgress } from '../print/print_plan_context.js?v=20260826b';
import { renderCoordinateAxisOverlay } from '../render/coordinate_axis_overlay.js?v=20260706d';
import * as ViewportController from '../viewport/viewport_controller.js?v=20260814b';
import * as AppOptionsController from './app_options_controller.js?v=20260904f';
import { renderObstacleSelectionPanel } from '../obstacles/obstacle_ui.js?v=20260903c';
import { syncObstacleToolbarButtons } from '../obstacles/obstacles.js?v=20260903c';
import { renderSketchWallPanel } from '../sketch_walls/sketch_wall_ui.js?v=20260903c';
import {
  renderObjectLibraryPanel,
  syncObjectLibraryToolbarButton
} from '../objects/object_library.js?v=20260814b';
import { syncProjectControls } from '../project/project_controls.js?v=20260903c';
import { scheduleOrderUpdateCheck } from '../project/order_update_service.mjs';
import * as SelectionService from '../selection/selection_service.js?v=20260904f';
import { syncFromLegacy as syncToolFromLegacy } from '../interaction/tool_service.js?v=20260904f';
import * as SystemRegistry from '../registry/system_registry.js?v=20260904f';
import {
  renderGlobalLevelsPanel,
  renderScenePresetControls
} from '../services/global_levels.js?v=20260903c';
import * as SidebarRegistry from '../sidebar/sidebar.js?v=20260904f';
import { S } from './app_state.js?v=20260817c';
import { syncProjectItems, syncProjectLinks } from '../project/project_state_service.js?v=20260904f';
import {
  flushAutomaticAreaMeasurementsAfterProjectChange
} from '../services/auto_area_measurement_service.js?v=20260814c';

function renderGlobalSidebarPanels() {
    if (deferShellRender('global-sidebar', renderGlobalSidebarPanels)) return;
    SidebarRegistry.renderGlobalPanels();
  }

function syncPlanViewState() {
    S.project.currentView = 'plan';
    document.getElementById('planSvg')?.classList.remove('hidden');
    if (!document.getElementById('threeDModalRoot')) document.getElementById('view3d')?.classList.add('hidden');
  }

function renderActiveSystemUi() {
    SystemRegistry.renderActiveUI();
  }

function syncLegacyServices() {
    syncProjectItems();
    syncProjectLinks();
    SelectionService.syncFromLegacy();
    syncToolFromLegacy();
  }

function stableSelectionValue(value) {
    if (Array.isArray(value)) {
      return value
        .map(entry => stableSelectionValue(entry))
        .sort();
    }
    if (value && typeof value === 'object') {
      return Object.keys(value)
        .sort()
        .reduce((result, key) => {
          const normalized = stableSelectionValue(value[key]);
          if (normalized === undefined) return result;
          result[key] = normalized;
          return result;
        }, {});
    }
    if (typeof value === 'function' || value === undefined) return undefined;
    return value;
  }

function rightSidebarSelectionSignature() {
    return JSON.stringify(stableSelectionValue(S.selection || {}));
  }

function closeLevelsPanelIfRightSidebarIsTaken() {
    if (!S.ui || typeof S.ui !== 'object') S.ui = {};
    if (!S.ui.showLevelsPanel) {
      delete S.ui.levelsPanelSelectionSignature;
      return false;
    }
    if (S.ui.showAppOptions) {
      delete S.ui.levelsPanelSelectionSignature;
      return AppRuntime.closeLevelsPanelForToolChange() === true;
    }
    if (S.ui.showAppAdminPanel) {
      delete S.ui.levelsPanelSelectionSignature;
      return AppRuntime.closeLevelsPanelForToolChange() === true;
    }
    const selectionSignature = rightSidebarSelectionSignature();
    if (typeof S.ui.levelsPanelSelectionSignature !== 'string') {
      S.ui.levelsPanelSelectionSignature = selectionSignature;
      return false;
    }
    if (selectionSignature === S.ui.levelsPanelSelectionSignature) return false;
    delete S.ui.levelsPanelSelectionSignature;
    return AppRuntime.closeLevelsPanelForToolChange() === true;
  }

function renderSidePanels() {
    if (deferShellRender('side-panels', renderSidePanels)) return;
    closeLevelsPanelIfRightSidebarIsTaken();
    renderGlobalSidebarPanels();
    if (S.ui?.showAppAdminPanel && !AppOptionsController.isAdminUser()) S.ui.showAppAdminPanel = false;
    if (S.ui?.showAppAdminPanel) AppOptionsController.renderAppAdminPanel();
    else if (S.ui?.showAppOptions) AppOptionsController.renderAppOptionsPanel();
    else if (!renderObjectLibraryPanel() && !renderSketchWallPanel()) renderObstacleSelectionPanel();
    AppOptionsController.syncAppOptionsButton();
    AppOptionsController.syncAppAdminButton();
    syncObstacleToolbarButtons();
    syncObjectLibraryToolbarButton();
    renderGlobalLevelsPanel();
    renderScenePresetControls();
  }

function refresh(fit = false) {
    if (isPreparingProjectMutation()) return;
    const nextFit = RenderScheduler.consumeAppRefreshFit(fit);
    RenderScheduler.cancelPlanRender();
    const profile = RenderScheduler.appPerfTimer('refresh');
    ViewportController.ensureCanvasLayout();
    syncPlanViewState();
    RenderScheduler.appPerfStep(profile, 'layout');
    SystemRegistry.renderShell();
    RenderScheduler.appPerfStep(profile, 'shell');
    if (nextFit) {
      SystemRegistry.fitActiveCamera();
    }
    RenderScheduler.appPerfStep(profile, 'fit');
    syncLegacyServices();
    RenderScheduler.appPerfStep(profile, 'state-sync');
    flushAutomaticAreaMeasurementsAfterProjectChange();
    RenderScheduler.appPerfStep(profile, 'measurement-geometry-sync');
    closeLevelsPanelIfRightSidebarIsTaken();
    RenderScheduler.appPerfStep(profile, 'levels-sidebar-state');
    renderActiveSystemUi();
    syncProjectControls();
    scheduleOrderUpdateCheck();
    RenderScheduler.appPerfStep(profile, 'active-ui');
    renderSidePanels();
    RenderScheduler.appPerfStep(profile, 'side-panels');
    ViewportController.restorePendingCanvasViewportLock();
    RenderScheduler.appPerfStep(profile, 'viewport-lock');
    syncPlanViewState();
    render2D();
    RenderScheduler.appPerfStep(profile, 'render2D');
    renderCoordinateAxisOverlay();
    AppRuntime.drawPlanInteractionOverlay();
    RenderScheduler.appPerfStep(profile, 'overlays');
    if (!isBomPlanCaptureInProgress() && !isPrintPlanCaptureInProgress()) AppRuntime.notifyPlanRefreshed({ source: 'refresh' });
    RenderScheduler.appPerfStep(profile, 'dispatch');
    RenderScheduler.appPerfEnd(profile);
  }

function refreshChromeOnly(reason = '') {
    if (isPreparingProjectMutation()) return;
    const profile = RenderScheduler.appPerfTimer(reason ? `refreshChromeOnly:${reason}` : 'refreshChromeOnly');
    ViewportController.ensureCanvasLayout();
    syncPlanViewState();
    RenderScheduler.appPerfStep(profile, 'layout');
    SystemRegistry.renderShell();
    RenderScheduler.appPerfStep(profile, 'shell');
    syncLegacyServices();
    RenderScheduler.appPerfStep(profile, 'state-sync');
    renderActiveSystemUi();
    syncProjectControls();
    scheduleOrderUpdateCheck();
    RenderScheduler.appPerfStep(profile, 'active-ui');
    renderSidePanels();
    RenderScheduler.appPerfStep(profile, 'side-panels');
    ViewportController.restorePendingCanvasViewportLock();
    RenderScheduler.appPerfStep(profile, 'viewport-lock');
    if (!isBomPlanCaptureInProgress() && !isPrintPlanCaptureInProgress()) renderActivePlanSurface({ chromeOnly: true });
    RenderScheduler.appPerfEnd(profile);
  }

function currentPlanRenderContext() {
    return {
      project: S.project,
      sheetId: S.drawingSheets?.activeSheetId,
      view: S.project?.currentView,
      drag: S.drag,
      dragActive: S.drag?.active === true,
      dragType: S.drag?.type,
      dragOwner: S.drag?.owner,
      dragSystemId: S.drag?.systemId
    };
  }

function renderPlanRequest(reason, request) {
    const profile = RenderScheduler.appPerfTimer(reason ? `renderPlanOnly:${reason}` : 'renderPlanOnly');
    const resolved = resolvePlanRenderRequest(request, currentPlanRenderContext());
    syncPlanViewState();
    render2D({
      navOnly: resolved.kind === 'navigation' && RenderScheduler.isPlanNavigationLodActive(),
      overlayOnly: resolved.kind === 'annotations',
      dragSystemId: resolved.kind === 'system-drag' ? resolved.systemId : null
    });
    RenderScheduler.appPerfStep(profile, 'render2D');
    renderCoordinateAxisOverlay();
    AppRuntime.drawPlanInteractionOverlay();
    RenderScheduler.appPerfStep(profile, 'overlays');
    RenderScheduler.appPerfEnd(profile);
  }

function renderPlanOnly(reason = '', options = {}) {
    if (isPreparingProjectMutation()) return;
    renderPlanRequest(reason, createPlanRenderRequest(options, currentPlanRenderContext()));
  }

function requestPlanRender(reason = '', options = {}) {
    if (isPreparingProjectMutation()) return;
    const request = createPlanRenderRequest(options, currentPlanRenderContext());
    RenderScheduler.requestPlanRender(mergedRequest => {
      renderPlanRequest(reason, mergedRequest);
    }, request);
  }

function requestIdlePlanRender(reason = '') {
    RenderScheduler.requestIdlePlanRender(mergedRequest => {
      renderPlanRequest(reason, mergedRequest);
    });
  }

function startPlanNavigationLod(reason = 'nav') {
    RenderScheduler.startPlanNavigationLod(() => requestIdlePlanRender(`${reason}-idle`), reason);
  }

function requestAppRefresh(fit = false, reason = '') {
    if (isPreparingProjectMutation()) return;
    RenderScheduler.requestAppRefresh(nextFit => {
      const profile = RenderScheduler.appPerfTimer(reason ? `scheduled-refresh:${reason}` : 'scheduled-refresh');
      refresh(nextFit);
      RenderScheduler.appPerfEnd(profile);
    }, fit);
  }

export {
  renderGlobalSidebarPanels,
  syncPlanViewState,
  refresh,
  refreshChromeOnly,
  renderPlanOnly,
  requestPlanRender,
  startPlanNavigationLod,
  requestAppRefresh
};
